import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_KEYMAP, type GameAction, type RenderMode } from '../engine/types';
import type { CameraMode } from '../render3d/cameraModes';

export interface PlayerPreferences {
  renderMode?: RenderMode;
  cameraMode?: Exclude<CameraMode, 'wide'>;
  keymap?: Partial<Record<GameAction, string>>;
  masterVolume?: number;
  muted?: boolean;
  effectsEnabled?: boolean;
  attractMotion?: boolean;
  selectedTableId?: string | null;
}

interface SettingsState {
  renderMode: RenderMode;
  renderModePreferenceSet: boolean;
  cameraMode: CameraMode;
  keymap: Record<GameAction, string>;
  masterVolume: number; // 0–1
  muted: boolean;
  effectsEnabled: boolean;
  attractMotion: boolean;
  selectedTableId: string | null;
  setRenderMode: (mode: RenderMode) => void;
  setCameraMode: (mode: CameraMode) => void;
  setKey: (action: GameAction, code: string) => void;
  setMasterVolume: (v: number) => void;
  setMuted: (muted: boolean) => void;
  toggleMute: () => void;
  setEffectsEnabled: (enabled: boolean) => void;
  setAttractMotion: (enabled: boolean) => void;
  setSelectedTableId: (tableId: string | null) => void;
  applyPreferences: (preferences: PlayerPreferences) => void;
}

/** User preferences persisted to localStorage. Game session state stays in-memory. */
export const useSettings = create<SettingsState>()(
  persist(
    (set) => ({
      renderMode: '3d',
      renderModePreferenceSet: false,
      cameraMode: 'close',
      keymap: { ...DEFAULT_KEYMAP },
      masterVolume: 0.8,
      muted: false,
      effectsEnabled: true,
      attractMotion: true,
      selectedTableId: null,
      setRenderMode: (mode) => set({ renderMode: mode, renderModePreferenceSet: true }),
      setCameraMode: (mode) => set({ cameraMode: mode === 'wide' ? 'normal' : mode }),
      setKey: (action, code) => set((s) => ({ keymap: { ...s.keymap, [action]: code } })),
      setMasterVolume: (v) => set({ masterVolume: Math.min(1, Math.max(0, v)) }),
      setMuted: (muted) => set({ muted }),
      toggleMute: () => set((s) => ({ muted: !s.muted })),
      setEffectsEnabled: (enabled) => set({ effectsEnabled: enabled }),
      setAttractMotion: (enabled) => set({ attractMotion: enabled }),
      setSelectedTableId: (selectedTableId) => set({ selectedTableId }),
      applyPreferences: (preferences) => set((state) => ({
        renderMode: preferences.renderMode ?? (state.renderModePreferenceSet ? state.renderMode : '3d'),
        renderModePreferenceSet: preferences.renderMode !== undefined || state.renderModePreferenceSet,
        cameraMode: preferences.cameraMode ?? (state.cameraMode === 'wide' ? 'normal' : state.cameraMode),
        keymap: preferences.keymap ? { ...state.keymap, ...preferences.keymap } : state.keymap,
        masterVolume: preferences.masterVolume === undefined
          ? state.masterVolume
          : Math.max(0, Math.min(1, preferences.masterVolume)),
        muted: preferences.muted ?? state.muted,
        effectsEnabled: preferences.effectsEnabled ?? state.effectsEnabled,
        attractMotion: preferences.attractMotion ?? state.attractMotion,
        selectedTableId: preferences.selectedTableId === undefined ? state.selectedTableId : preferences.selectedTableId,
      })),
    }),
    {
      name: 'bop-settings',
      version: 9,
      migrate: (persistedState, version) => {
        const state = persistedState as Partial<SettingsState>;
        const normalizedCamera = state.cameraMode === 'wide'
          || (version < 3 && (state.cameraMode === 'close' || state.cameraMode === 'normal' || !state.cameraMode))
          ? 'normal' as CameraMode
          : state.cameraMode;
        const migratedCamera = version < 9 && normalizedCamera === 'normal'
          ? 'close' as CameraMode
          : normalizedCamera;

        if (version < 8) {
          return {
            ...state,
            cameraMode: migratedCamera,
            renderMode: '3d' as RenderMode,
            renderModePreferenceSet: false,
          };
        }
        return { ...state, cameraMode: migratedCamera };
      },
    },
  ),
);

/** Serializable preference snapshot suitable for D1 account sync. */
export function currentPlayerPreferences(): PlayerPreferences {
  const state = useSettings.getState();
  return {
    renderMode: state.renderModePreferenceSet ? state.renderMode : undefined,
    cameraMode: state.cameraMode === 'wide' ? 'normal' : state.cameraMode,
    keymap: state.keymap,
    masterVolume: state.masterVolume,
    muted: state.muted,
    effectsEnabled: state.effectsEnabled,
    attractMotion: state.attractMotion,
    selectedTableId: state.selectedTableId,
  };
}

interface SessionState {
  score: number;
  multiplier: number;
  nudgesRemaining: number;
  ballsRemaining: number;
  ballSaver: boolean;
  tilted: boolean;
  highScoreInitials: string;
  setSession: (p: Partial<Omit<SessionState, 'setSession'>>) => void;
}

export const useSession = create<SessionState>()((set) => ({
  score: 0,
  multiplier: 1,
  nudgesRemaining: 3,
  ballsRemaining: 3,
  ballSaver: false,
  tilted: false,
  highScoreInitials: '',
  setSession: (patch) => set((state) => {
    const unchanged =
      (patch.score === undefined || patch.score === state.score)
      && (patch.multiplier === undefined || patch.multiplier === state.multiplier)
      && (patch.nudgesRemaining === undefined || patch.nudgesRemaining === state.nudgesRemaining)
      && (patch.ballsRemaining === undefined || patch.ballsRemaining === state.ballsRemaining)
      && (patch.ballSaver === undefined || patch.ballSaver === state.ballSaver)
      && (patch.tilted === undefined || patch.tilted === state.tilted)
      && (patch.highScoreInitials === undefined || patch.highScoreInitials === state.highScoreInitials);
    return unchanged ? state : patch;
  }),
}));
